/**
 * 將喜歡/收藏等計數格式化為中文單位顯示（N1-5190）。
 *
 * 規則：
 * - `0 ~ 9999`：顯示整數原值（最多 4 位數）
 * - `≥ 10000`：以「万」為單位；當前單位數值超過 `999.9` 即進位到下一單位（每階 ×1000）
 * - 四捨五入到小數點一位（標準四捨五入），整數時去掉 `.0`
 * - 四捨五入後若該單位數值 `≥ 1000` 且有更高單位 → 進位（如 `9,999,999` → `1千万`）
 * - 異常值（`null` / `undefined` / `NaN` / 字串無法轉數字 / 負數）→ `'0'`
 *
 * 單位階梯（純 ×1000；依需求 Q1「999.9 才跨單位」之規則，每一單位皆顯示 1.0~999.9 後跨單位。
 * 原始需求文字列舉的「亿」在此 ×1000 規則下對應的是 10^10＝「百亿」。
 * like/favorite 計數實務上幾乎不可能超過「千万」(10^7)，10^8 以上純為防禦性分支，正常情境不會觸及）：
 *
 * | 單位 | 數量級 | 顯示範圍       |
 * |------|--------|----------------|
 * | （無）| 10^0   | 0 ~ 9999       |
 * | 万   | 10^4   | 1万 ~ 999.9万   |
 * | 千万 | 10^7   | 1千万 ~ 999.9千万 |
 * | 百亿 | 10^10  | 1百亿 ~ ...（防禦性）|
 *
 * 假設：僅適用中文品牌（kuaibo / bense / saohu / 69 / 51lu 皆為中文站）。
 * 若未來上線非中文語系品牌，需另設語系對應的格式化工具。
 *
 * 範例對照（規格化期望輸出）：
 * | 輸入       | 輸出      |
 * |------------|-----------|
 * | 0          | `0`       |
 * | 8888       | `8888`    |
 * | 9999       | `9999`    |
 * | 10000      | `1万`     |
 * | 13000      | `1.3万`   |
 * | 99999      | `10万`    |
 * | 9999000    | `999.9万` |
 * | 9999999    | `1千万`   |
 * | 13000000   | `1.3千万` |
 */

interface CountUnit {
  divisor: number
  suffix: string
}

// 由小到大；每階 ×1000
const UNITS: readonly CountUnit[] = [
  { divisor: 1e4, suffix: '万' },
  { divisor: 1e7, suffix: '千万' },
  { divisor: 1e10, suffix: '百亿' },
]

const BASE_MAX = 9999

/** 四捨五入到小數點一位；整數時透過 Number→String 自動去掉 `.0`（不可用 toFixed）。 */
const roundToOneDecimal = (value: number): number => Math.round(value * 10) / 10

export const formatCountCN = (val: number | string | null | undefined): string => {
  const n = typeof val === 'number' ? val : Number(val)
  if (!Number.isFinite(n) || n <= 0) return '0'

  // count 實務為整數（API 型別為 number）；以 Math.round 取整，對整數輸入等同原值，並對極端小數輸入更精準
  const value = Math.round(n)
  if (value <= BASE_MAX) return String(value)

  // 由最大單位往下找第一個適用者，並處理四捨五入後的溢位進位
  for (let i = UNITS.length - 1; i >= 0; i--) {
    const { divisor, suffix } = UNITS[i]
    if (value < divisor) continue

    const rounded = roundToOneDecimal(value / divisor)
    const nextUnit = UNITS[i + 1]
    // 四捨五入後 ≥ 1000 且還有更高單位 → 進位（例：9,999,999 → 1千万）
    if (rounded >= 1000 && nextUnit) {
      return `${roundToOneDecimal(value / nextUnit.divisor)}${nextUnit.suffix}`
    }
    return `${rounded}${suffix}`
  }

  // 不會走到（value > BASE_MAX 必 ≥ 最小單位 divisor）
  return String(value)
}
